import { useCallback, useEffect, useState } from 'react';

import { ensureMediaPermissions } from '../../../utils';
import { isHandledMediaAccessError } from '../../../utils/mediaAccessErrors';
import Video, {
  LocalVideoTrack,
  LocalAudioTrack,
  CreateLocalTrackOptions,
  LocalDataTrack,
  LocalDataTrackOptions,
} from 'twilio-video';

const logUnexpectedLocalTrackError = (trackType: 'audio' | 'video', error: unknown) => {
  console.error(`Failed to initialize local ${trackType} track.`, error);
};

export function useLocalAudioTrack() {
  const [track, setTrack] = useState<LocalAudioTrack>();

  const getLocalAudioTrack = useCallback((deviceId?: string) => {
    const options: CreateLocalTrackOptions = {};

    if (deviceId) {
      options.deviceId = { exact: deviceId };
    }

    return ensureMediaPermissions().then(() =>
      Video.createLocalAudioTrack(options).then(newTrack => {
        setTrack(newTrack);
        return newTrack;
      })
    );
  }, []);

  useEffect(() => {
    void getLocalAudioTrack().catch(error => {
      if (isHandledMediaAccessError(error)) {
        setTrack(undefined);
        return;
      }

      logUnexpectedLocalTrackError('audio', error);
    });
  }, [getLocalAudioTrack]);

  useEffect(() => {
    const handleStopped = () => setTrack(undefined);
    if (track) {
      track.on('stopped', handleStopped);
      return () => {
        track.off('stopped', handleStopped);
      };
    }
  }, [track]);

  return [track, getLocalAudioTrack] as const;
}

export function useLocalVideoTrack() {
  const [track, setTrack] = useState<LocalVideoTrack>();

  const getLocalVideoTrack = useCallback((newOptions?: CreateLocalTrackOptions) => {
    // In the DeviceSelector and FlipCameraButton components, a new video track is created,
    // then the old track is unpublished and the new track is published. Unpublishing the old
    // track and publishing the new track at the same time sometimes causes a conflict when the
    // track name is 'camera', so here we append a timestamp to the track name to avoid the
    // conflict.
    let height = window.innerHeight;
    let width = (4 / 3) * height;
    if (width > window.innerWidth) {
      height = (3 / 4) * window.innerWidth;
      width = window.innerWidth;
    }
    const options: CreateLocalTrackOptions = {
      frameRate: 24,
      height: 720, //parseInt(relheight.substring(0, relheight.length - 2)),
      width: 1080, //with a 4/3 ratio width is 4/3*height.
      name: `camera-${Date.now()}`,
      ...newOptions,
    };

    return ensureMediaPermissions().then(() =>
      Video.createLocalVideoTrack(options).then(newTrack => {
        setTrack(newTrack);
        return newTrack;
      })
    );
  }, []);

  useEffect(() => {
    // We get a new local video track when the app loads.
    void getLocalVideoTrack().catch(error => {
      if (isHandledMediaAccessError(error)) {
        setTrack(undefined);
        return;
      }

      logUnexpectedLocalTrackError('video', error);
    });
  }, [getLocalVideoTrack]);

  useEffect(() => {
    const handleStopped = () => setTrack(undefined);
    if (track) {
      track.on('stopped', handleStopped);
      return () => {
        track.off('stopped', handleStopped);
      };
    }
  }, [track]);

  return [track, getLocalVideoTrack] as const;
}

export function useLocalDataTrack() {
  const [track, setTrack] = useState<LocalDataTrack>();

  const getLocalDataTrack = useCallback((newOptions?: LocalDataTrackOptions) => {
    const localDataTrack = new LocalDataTrack(newOptions);
    setTrack(localDataTrack);
    return localDataTrack;
  }, []);

  useEffect(() => {
    // We get a new local data track when the app loads.
    getLocalDataTrack();
  }, [getLocalDataTrack]);

  return [track, getLocalDataTrack] as const;
}

export default function useLocalTracks() {
  const [audioTrack, getLocalAudioTrack] = useLocalAudioTrack();
  const [videoTrack, getLocalVideoTrack] = useLocalVideoTrack();
  const [dataTrack, getLocalDataTrack] = useLocalDataTrack();

  const localTracks = [audioTrack, videoTrack, dataTrack].filter(track => track !== undefined) as (
    | LocalAudioTrack
    | LocalVideoTrack
    | LocalDataTrack
  )[];

  return { localTracks, getLocalVideoTrack, getLocalAudioTrack, getLocalDataTrack };
}
